// frontend/src/pages/teams/TeamRegistrationSheet.jsx

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import teamService from '../../services/teamService';
import championshipService from '../../services/championshipService';
import './TeamRegistrationSheet.css';

const getImageUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace('/api', '');
  return `${baseUrl}${path.startsWith('/') ? '' : '/'}${path}`;
};

const formatPhone = (phoneString) => {
  if (!phoneString) return '';
  const cleaned = String(phoneString).replace(/\D/g, '');
  if (cleaned.length === 11) {
    const match = cleaned.match(/^(\d{2})(\d{5})(\d{4})$/);
    if (match) return `(${match[1]}) ${match[2]}-${match[3]}`;
  }
  if (cleaned.length === 10) {
    const match = cleaned.match(/^(\d{2})(\d{4})(\d{4})$/);
    if (match) return `(${match[1]}) ${match[2]}-${match[3]}`;
  }
  return phoneString;
};

// ✅ Helper para escolher a copa recente caso nenhuma venha na URL
const getLatestChampionship = (championships) => {
  if (!championships || championships.length === 0) return null;
  const active = championships.find(c => c.status === 'Em andamento' || c.registrationOpen === true);
  if (active) return active;
  return championships[championships.length - 1]; // Pega a mais recente
};

const getColumnWidths = (includePhone, includeSignature, includePosition = false, isTechnical = false) => {
  if (isTechnical) {
    if (includePhone && includeSignature) {
      return { num: '6%', name: '33%', position: '19%', phone: '23%', signature: '19%' };
    }
    if (includePhone && !includeSignature) {
      return { num: '6%', name: '48%', position: '19%', phone: '27%' };
    }
    if (!includePhone && includeSignature) {
      return { num: '6%', name: '48%', position: '19%', signature: '27%' };
    }
    return { num: '6%', name: '75%', position: '19%' };
  } else {
    // Tabela de Jogadores com suporte a Posição Opcional
    if (includePosition) {
      if (includePhone && includeSignature) {
        return { num: '6%', name: '36%', position: '16%', phone: '22%', signature: '20%' };
      }
      if (includePhone && !includeSignature) {
        return { num: '6%', name: '52%', position: '18%', phone: '24%' };
      }
      if (!includePhone && includeSignature) {
        return { num: '6%', name: '58%', position: '16%', signature: '20%' };
      }
      return { num: '6%', name: '76%', position: '18%' };
    }

    if (includePhone && includeSignature) {
      return { num: '6%', name: '48%', phone: '26%', signature: '20%' };
    }
    if (includePhone && !includeSignature) {
      return { num: '6%', name: '68%', phone: '26%' };
    }
    if (!includePhone && includeSignature) {
      return { num: '6%', name: '74%', signature: '20%' };
    }
    return { num: '6%', name: '94%' };
  }
};

function TeamRegistrationSheet() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ Lê o championshipId vindo do state OU da URL (?championshipId=...)
  const queryParams = new URLSearchParams(location.search);
  const routeChampionshipId = location.state?.championshipId || queryParams.get('championshipId') || null;

  const includePhone = location.state?.includePhone ?? true;
  const includeSignature = location.state?.includeSignature ?? true;
  const includePosition = location.state?.includePosition ?? false;

  const [team, setTeam] = useState(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [teamImgSrc, setTeamImgSrc] = useState(null);
  const [championshipName, setChampionshipName] = useState('CAMPEONATO');
  const [championshipLogo, setChampionshipLogo] = useState(null);
  const [championshipHeaderImage, setChampionshipHeaderImage] = useState('');

  const MAX_PLAYERS_PER_SHEET   = 25;
  const MAX_TECHNICAL_PER_SHEET = 5;

  const techWidths = getColumnWidths(includePhone, includeSignature, false, true);
  const playerWidths = getColumnWidths(includePhone, includeSignature, includePosition, false);

  useEffect(() => {
    const fetchTeamAndChampionshipData = async () => {
      if (!id) { setError('ID do time não fornecido.'); setIsLoading(false); return; }
      setIsLoading(true);
      setError('');
      try {
        let activeChampId = routeChampionshipId;

        // 1. Busca os dados do time passando o ID da copa (IGUAL AO MOBILE)
        let teamData = await teamService.getTeamById(id, activeChampId);

        if (teamData) {
          // Se não veio copa na URL, acha a copa recente do time e busca os atletas dela
          if (!activeChampId && teamData.championships && teamData.championships.length > 0) {
            const latestChamp = getLatestChampionship(teamData.championships);
            activeChampId = latestChamp?._id;
            if (activeChampId) {
              teamData = await teamService.getTeamById(id, activeChampId);
            }
          }

          setTeam(teamData);
          setTeamImgSrc(getImageUrl(teamData.badge) || null);

          // 2. Busca informações do cabeçalho da copa certa
          const currentChamp = teamData.championships?.find(c => String(c._id || c) === String(activeChampId)) || teamData.championships?.[teamData.championships.length - 1];
          const champIdToFetch = currentChamp?._id || activeChampId;

          if (champIdToFetch) {
            try {
              const champResponse = await championshipService.getById(champIdToFetch);
              const champData = champResponse.data || champResponse;
              if (champData) {
                setChampionshipName(champData.name?.toUpperCase() || 'CAMPEONATO');
                setChampionshipLogo(getImageUrl(champData.logo) || null);
                if (champData.headerImage) setChampionshipHeaderImage(getImageUrl(champData.headerImage));
              }
            } catch {
              setChampionshipName(currentChamp?.name?.toUpperCase() || 'CAMPEONATO');
            }
          }
        } else {
          setError('Time não encontrado');
        }
      } catch (err) {
        setError(err.response?.data?.message || err.message || 'Erro ao carregar dados.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchTeamAndChampionshipData();
  }, [id, routeChampionshipId]);

  const handleTeamImageError         = (e) => { e.target.style.display = 'none'; };
  const handleChampionshipLogoError  = (e) => { e.target.style.display = 'none'; };

  const isTechnicalStaff = (position) => {
    if (!position) return false;
    const pos = position.toLowerCase().trim();
    return (
      pos.includes('técnico') || pos.includes('tecnico') ||
      pos.includes('auxiliar') || pos.includes('aux.') ||
      pos === 'diretor'        || pos.includes('diretor')
    );
  };

  const sortTechnicalStaff = (a, b) => {
    const posA = a?.position?.toLowerCase().trim() || '';
    const posB = b?.position?.toLowerCase().trim() || '';
    const getPriority = (pos) => {
      if ((pos.includes('técnico') || pos.includes('tecnico')) && !pos.includes('auxiliar')) return 1;
      if (pos.includes('auxiliar') || pos.includes('aux.')) return 2;
      if (pos === 'diretor' || pos.includes('diretor')) return 3;
      return 4;
    };
    const pA = getPriority(posA), pB = getPriority(posB);
    if (pA !== pB) return pA - pB;
    return (a?.name || '').localeCompare(b?.name || '');
  };

  // ✅ Filtra atletas únicos garantindo que não haja repetições
  const uniqueMembers = useMemo(() => {
    if (!team?.players || !Array.isArray(team.players)) return [];
    const map = new Map();
    team.players.forEach(p => {
      if (!p) return;
      const key = p._id || p.id || p.cpf || p.name;
      if (key && !map.has(key)) {
        map.set(key, p);
      }
    });
    return Array.from(map.values());
  }, [team?.players]);

  const getDisplayList = (items, maxItems) => {
    const sorted = [...items].sort(sortTechnicalStaff);
    const sliced = sorted.slice(0, maxItems);
    return sliced.length < maxItems
      ? [...sliced, ...Array(maxItems - sliced.length).fill(null)]
      : sliced;
  };

  const findResponsibleName = () => {
    if (!uniqueMembers.length) return '';
    const technical = uniqueMembers.filter(p => isTechnicalStaff(p.position));
    const diretor  = technical.find(p => p.position?.toLowerCase().includes('diretor'));
    if (diretor) return diretor.name;
    const tecnico  = technical.find(p =>
      (p.position?.toLowerCase().includes('técnico') || p.position?.toLowerCase().includes('tecnico')) &&
      !p.position.toLowerCase().includes('auxiliar')
    );
    if (tecnico) return tecnico.name;
    const auxiliar = technical.find(p => p.position?.toLowerCase().includes('auxiliar'));
    if (auxiliar) return auxiliar.name;
    return team?.responsibleName || team?.coachName || '';
  };

  if (isLoading) return <div className="sheet-loading">Carregando ficha de inscrição…</div>;

  if (error || !team) {
    return (
      <div className="sheet-error">
        <h2>Erro ao Gerar Ficha</h2>
        <p>{error || 'Time não encontrado.'}</p>
        <button onClick={() => navigate(-1)} className="sheet-button secondary">Voltar</button>
      </div>
    );
  }

  const technicalStaffList = uniqueMembers.filter(p => isTechnicalStaff(p.position));
  const playersList        = uniqueMembers.filter(p => !isTechnicalStaff(p.position));

  const displayTechnical = getDisplayList(technicalStaffList, MAX_TECHNICAL_PER_SHEET);
  const displayPlayers   = getDisplayList(playersList,        MAX_PLAYERS_PER_SHEET);
  const responsibleName  = findResponsibleName();

  return (
    <div className="registration-sheet-page printable-area">
      <div className="sheet-document">

        {/* ── CABEÇALHO ── */}
        <header className="sheet-header">
          {championshipHeaderImage && (
            <img
              src={championshipHeaderImage}
              alt="Cabeçalho do Campeonato"
              className="sheet-header-image"
              onError={handleChampionshipLogoError}
            />
          )}
          <div className="header-main-content">
            <div className="sheet-logo-container">
              {teamImgSrc && (
                <img
                  src={teamImgSrc}
                  alt={`Escudo ${team.name}`}
                  className="sheet-team-logo"
                  onError={handleTeamImageError}
                />
              )}
            </div>
            <div className="sheet-title-container">
              <h1>FICHA DE INSCRIÇÃO DE ATLETAS</h1>
              <h2>{championshipName}</h2>
            </div>
            <div className="sheet-logo-container">
              {championshipLogo && (
                <img
                  src={championshipLogo}
                  alt={`Logo ${championshipName}`}
                  className="sheet-team-logo"
                  onError={handleChampionshipLogoError}
                />
              )}
            </div>
          </div>
        </header>

        {/* ── INFO DO TIME ── */}
        <section className="team-info-section">
          <div className="info-field">
            <label>NOME DA EQUIPE</label>
            <span>{team.name?.toUpperCase()}</span>
          </div>
          <div className="info-field">
            <label>RESPONSÁVEL</label>
            <span>{responsibleName?.toUpperCase()}</span>
          </div>
          <div className="info-field">
            <label>CONTATO</label>
            <span>{formatPhone(team.phone || team.contactInfo?.phone || team.contact || '')}</span>
          </div>
        </section>

        {/* ── COMISSÃO TÉCNICA ── */}
        <section className="technical-staff-section">
          <h3 className="section-subtitle">COMISSÃO TÉCNICA</h3>
          <table>
            <thead>
              <tr>
                <th style={{ width: techWidths.num }}>Nº</th>
                <th style={{ width: techWidths.name }}>NOME COMPLETO</th>
                <th style={{ width: techWidths.position }}>FUNÇÃO</th>
                {includePhone     && <th style={{ width: techWidths.phone }}>TELEFONE</th>}
                {includeSignature && <th style={{ width: techWidths.signature }}>ASSINATURA</th>}
              </tr>
            </thead>
            <tbody>
              {displayTechnical.map((staff, index) => (
                <tr key={staff?._id || `tech-${index}`}>
                  <td style={{ width: techWidths.num, textAlign: 'center' }}>
                    {staff ? index + 1 : ''}
                  </td>
                  <td style={{ width: techWidths.name }}>
                    {staff?.name || '\u00A0'}
                  </td>
                  <td style={{ width: techWidths.position }}>
                    {staff?.position || '\u00A0'}
                  </td>
                  {includePhone && (
                    <td style={{ width: techWidths.phone, textAlign: 'center' }}>
                      {staff ? formatPhone(staff.phone) : '\u00A0'}
                    </td>
                  )}
                  {includeSignature && (
                    <td style={{ width: techWidths.signature }}>{'\u00A0'}</td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {/* ── JOGADORES ── */}
        <section className="players-table-section">
          <h3 className="section-subtitle">JOGADORES</h3>
          <table>
            <thead>
              <tr>
                <th style={{ width: playerWidths.num }}>Nº</th>
                <th style={{ width: playerWidths.name }}>NOME COMPLETO DO ATLETA</th>
                {includePosition  && <th style={{ width: playerWidths.position }}>POSIÇÃO</th>}
                {includePhone     && <th style={{ width: playerWidths.phone }}>TELEFONE</th>}
                {includeSignature && <th style={{ width: playerWidths.signature }}>ASSINATURA</th>}
              </tr>
            </thead>
            <tbody>
              {displayPlayers.map((player, index) => (
                <tr key={player?._id || `player-${index}`}>
                  <td style={{ width: playerWidths.num, textAlign: 'center' }}>
                    {player ? index + 1 : ''}
                  </td>
                  <td style={{ width: playerWidths.name }}>
                    {player?.name || '\u00A0'}
                  </td>
                  {includePosition && (
                    <td style={{ width: playerWidths.position, textAlign: 'center' }}>
                      {player?.position || '\u00A0'}
                    </td>
                  )}
                  {includePhone && (
                    <td style={{ width: playerWidths.phone, textAlign: 'center' }}>
                      {player ? formatPhone(player.phone) : '\u00A0'}
                    </td>
                  )}
                  {includeSignature && (
                    <td style={{ width: playerWidths.signature }}>{'\u00A0'}</td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {/* ── RODAPÉ ── */}
        <footer className="sheet-footer">
          <div className="footer-signature-area">
            <p></p>
            <p>Assinatura do Responsável pela Equipe</p>
          </div>
          <div className="footer-date-area">
            <p>Data: ______ / ______ / __________</p>
          </div>
        </footer>
      </div>

      {/* ── BOTÕES ── */}
      <div className="sheet-actions-footer">
        <button onClick={() => navigate(-1)} className="sheet-button secondary">
          Voltar
        </button>
        <button
          onClick={() => setTimeout(() => window.print(), 300)}
          className="sheet-button primary"
        >
          ⬇ Gerar PDF
        </button>
      </div>
    </div>
  );
}

export default TeamRegistrationSheet;